import React, { useEffect, useRef, useState } from 'react';
import { setLanguage, useLang, type Lang } from '../i18n';

/* Small embedded flag graphics — drawn, not emoji (flag emoji don't render on
   every OS, e.g. Windows). Each is a 3:2 flag in a 24×16 viewBox. */
const FlagEN = ({ className = '' }: { className?: string }) => (
  <svg width="22" height="15" viewBox="0 0 24 16" overflow="hidden" className={className} aria-hidden>
    <rect width="24" height="16" fill="#012169" />
    <path d="M0 0L24 16M24 0L0 16" stroke="#ffffff" strokeWidth="3.4" />
    <path d="M0 0L24 16M24 0L0 16" stroke="#C8102E" strokeWidth="1.4" />
    <rect x="9.4" width="5.2" height="16" fill="#ffffff" />
    <rect y="5.4" width="24" height="5.2" fill="#ffffff" />
    <rect x="10.5" width="3" height="16" fill="#C8102E" />
    <rect y="6.5" width="24" height="3" fill="#C8102E" />
  </svg>
);

const FlagTR = ({ className = '' }: { className?: string }) => (
  <svg width="22" height="15" viewBox="0 0 24 16" overflow="hidden" className={className} aria-hidden>
    <rect width="24" height="16" fill="#E30A17" />
    <circle cx="9" cy="8" r="3.8" fill="#ffffff" />
    <circle cx="10.2" cy="8" r="3" fill="#E30A17" />
    <polygon
      transform="translate(14.4 8) scale(2.05)"
      points="0,-1 0.2245,-0.309 0.951,-0.309 0.3633,0.118 0.5878,0.809 0,0.382 -0.5878,0.809 -0.3633,0.118 -0.951,-0.309 -0.2245,-0.309"
      fill="#ffffff"
    />
  </svg>
);

type Entry = { code: Lang; label: string; Flag: React.FC<{ className?: string }> };
const LANGS: Entry[] = [
  { code: 'en', label: 'English', Flag: FlagEN },
  { code: 'tr', label: 'Türkçe', Flag: FlagTR },
];

/**
 * Compact flag dropdown for switching language (EN/TR). The trigger shows the
 * current flag + a chevron; the menu lists the embedded-flag options. Switching
 * is instant (see `setLanguage`/`useLang`) and persists. Designed to sit small
 * in a corner — the parent positions it (e.g. top-right).
 */
export default function LanguageToggle({ className = '' }: { className?: string }) {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [open]);

  const current = LANGS.find((l) => l.code === lang) ?? LANGS[0];

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Language"
        onClick={() => setOpen((o) => !o)}
        className="touch-target flex items-center gap-1.5 rounded-lg border border-[var(--border)] bg-[var(--card)] px-2 py-1.5 shadow-[var(--card-shadow)] transition-colors active:border-[var(--gold-lt)]"
      >
        <current.Flag className="rounded-[2px]" />
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--muted-lt)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`} aria-hidden>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <ul role="listbox" className="absolute right-0 top-[calc(100%+6px)] z-50 min-w-[8.5rem] overflow-hidden rounded-lg border border-[var(--border)] bg-[var(--card)] shadow-[0_8px_28px_rgba(0,0,0,0.5)]">
          {LANGS.map((l) => {
            const active = l.code === lang;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onClick={() => { setLanguage(l.code); setOpen(false); }}
                  className={`touch-target flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-[0.9rem] font-medium transition-colors ${
                    active ? 'bg-[var(--accent-pressed-bg)] text-[var(--cream)]' : 'text-[var(--muted-lt)] active:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <l.Flag className="rounded-[2px]" />
                  <span>{l.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
